import { motion } from "framer-motion";
import { MapPin, ShieldCheck, Waves, Wind } from "lucide-react";
import TaranakiMap from "@/components/TaranakiMap";

const SUBURBS = [
  { name: "New Plymouth", testid: "suburb-item-new-plymouth" },
  { name: "Bell Block", testid: "suburb-item-bell-block" },
  { name: "Waitara", testid: "suburb-item-waitara" },
  { name: "Inglewood", testid: "suburb-item-inglewood" },
  { name: "Stratford", testid: "suburb-item-stratford" },
  { name: "Hāwera", testid: "suburb-item-hawera" },
  { name: "Ōakura", testid: "suburb-item-oakura" },
  { name: "Ōpunake", testid: "suburb-item-opunake" },
];

const PROMISES = [
  { icon: Wind, label: "Wind-rated workmanship" },
  { icon: Waves, label: "Coastal salt protection" },
  { icon: ShieldCheck, label: "Tidy site, every time" },
];

export default function Suburbs() {
  return (
    <section id="coverage" className="relative border-t border-white/[0.06] bg-[#0D1320] py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-orange-400">03 — Coverage</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            Every corner of
            <br />
            the maunga.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
            Based in Taranaki and servicing the whole region — from the coast at Ōakura to the
            inland towns under the mountain. If you're in Taranaki, we'll come to you.
          </p>
          <div className="mt-10 space-y-4">
            {PROMISES.map((p) => (
              <div key={p.label} className="flex items-center gap-4 border border-white/[0.08] bg-[#131924] px-5 py-4">
                <p.icon className="h-5 w-5 shrink-0 text-orange-400" />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="suburbs-grid">
            {SUBURBS.map((s, i) => (
              <motion.div
                key={s.name}
                data-testid={s.testid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group flex items-center gap-3 border border-white/[0.08] bg-[#131924] px-5 py-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-orange-500/40"
              >
                <MapPin className="h-4 w-4 shrink-0 text-orange-500 transition-transform duration-300 group-hover:-translate-y-0.5" />
                <span className="font-heading text-sm font-semibold text-slate-200">{s.name}</span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: SUBURBS.length * 0.06 }}
              className="flex items-center justify-center border border-dashed border-orange-500/40 px-5 py-6"
            >
              <span className="text-center font-mono text-xs uppercase tracking-[0.2em] text-orange-400">
                + rural Taranaki
              </span>
            </motion.div>
          </div>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Fast response across the region — storm damage prioritised
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <TaranakiMap />
      </div>
    </section>
  );
}
